import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SubscriptionTier, TIER_NAMES, TIER_PRICING, formatCurrency } from '@/lib/subscriptionFeatures';
import { ArrowRight, Check } from 'lucide-react';

interface UpgradeRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId?: string;
  currentTier: SubscriptionTier;
  suggestedTier?: SubscriptionTier;
}

export function UpgradeRequestDialog({ 
  open, 
  onOpenChange, 
  companyId, 
  currentTier,
  suggestedTier 
}: UpgradeRequestDialogProps) {
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>(suggestedTier || 'professional');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableTiers: SubscriptionTier[] = ['professional', 'enterprise', 'enterprise_plus']
    .filter(t => {
      const tierOrder = { free: 0, professional: 1, enterprise: 2, enterprise_plus: 3 };
      return tierOrder[t as SubscriptionTier] > tierOrder[currentTier];
    }) as SubscriptionTier[];

  const handleSubmitRequest = async () => {
    if (!companyId) {
      toast.error('Company not found');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({
          upgrade_requested_tier: selectedTier,
          upgrade_requested_at: new Date().toISOString(),
        })
        .eq('id', companyId);

      if (error) throw error;

      toast.success('Upgrade request submitted', {
        description: 'Our team will review your request and contact you shortly.'
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting upgrade request:', error);
      toast.error('Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Plan Upgrade</DialogTitle>
          <DialogDescription>
            You're currently on {TIER_NAMES[currentTier]}. Select the plan you'd like to upgrade to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {availableTiers.length === 0 ? (
            <p className="text-sm text-muted-foreground">You're already on our highest tier.</p>
          ) : (
            <RadioGroup value={selectedTier} onValueChange={(v) => setSelectedTier(v as SubscriptionTier)}>
              {availableTiers.map((tier) => (
                <div key={tier} className="flex items-center space-x-3 p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                  <RadioGroupItem value={tier} id={tier} />
                  <Label htmlFor={tier} className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{TIER_NAMES[tier]}</p>
                        <p className="text-sm text-muted-foreground">
                          {TIER_PRICING[tier].monthly > 0 
                            ? `${formatCurrency(TIER_PRICING[tier].monthly)}/month`
                            : 'Custom pricing'
                          }
                        </p>
                      </div>
                      {tier === suggestedTier && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                          Recommended
                        </span>
                      )}
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitRequest} 
            disabled={isSubmitting || availableTiers.length === 0}
            className="gap-2"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Topup request dialog
interface TopupRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId?: string;
}

export function TopupRequestDialog({ open, onOpenChange, companyId }: TopupRequestDialogProps) {
  const [amount, setAmount] = useState(100);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const amounts = [100, 500, 1000, 2500];

  const handleSubmitRequest = async () => {
    if (!companyId) {
      toast.error('Company not found');
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({
          topup_requested_amount: amount,
          topup_requested_at: new Date().toISOString(),
        })
        .eq('id', companyId);

      if (error) throw error;

      toast.success('Top-up request submitted', {
        description: `Request for ${amount} conversations sent for approval.`
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting topup request:', error);
      toast.error('Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request AI Conversation Top-up</DialogTitle>
          <DialogDescription>
            Select the number of additional conversations you need.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup value={String(amount)} onValueChange={(v) => setAmount(Number(v))}>
            {amounts.map((a) => (
              <div key={a} className="flex items-center space-x-3 p-4 rounded-lg border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value={String(a)} id={`amount-${a}`} />
                <Label htmlFor={`amount-${a}`} className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">{a} conversations</p>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(a * 2000)}
                    </p>
                  </div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitRequest} 
            disabled={isSubmitting}
            className="gap-2"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
